/**
 * Popups ancrées à un élément zoomé (tailles d'interface de l'outil dessin,
 * cf. useCanvasUiScale) sur un navigateur qui applique encore l'ANCIEN `zoom`
 * CSS : Safari (iPad) et les Chromium antérieurs à la version 128 (navigateurs
 * de tablettes Android). Chez eux, getBoundingClientRect() d'un élément zoomé
 * rend ses coordonnées divisées par son zoom. Popper, qui place la popup dans
 * <body> (non zoomé), la pose alors au mauvais endroit : appui long sur le
 * crayon → infobulle en face de l'ellipse, voire hors de l'écran pour les
 * outils du bas.
 *
 * Le modificateur ramène le rectangle de l'élément d'ancrage dans le repère de
 * la popup. Sans effet sur un navigateur à jour (zoom standardisé) ou quand
 * l'ancrage et la popup ont le même zoom.
 */

interface PopperRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

let standardZoom: boolean | undefined;

/**
 * Le navigateur applique-t-il le `zoom` CSS standardisé ? Un élément de 10px
 * zoomé ×2 mesure 20px dans getBoundingClientRect() ; l'ancien zoom le rend à
 * 10px.
 */
export function hasStandardCssZoom(): boolean {
  if (standardZoom !== undefined) return standardZoom;
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:absolute;visibility:hidden;width:10px;height:10px;zoom:2";
  document.body.appendChild(probe);
  standardZoom = Math.round(probe.getBoundingClientRect().width) !== 10;
  probe.remove();
  return standardZoom;
}

/** Tests : oublie le résultat mémorisé de hasStandardCssZoom(). */
export function resetCssZoomProbe(): void {
  standardZoom = undefined;
}

/** Zoom cumulé de l'élément et de ses ancêtres. */
export function effectiveZoom(element: Element): number {
  let zoom = 1;
  for (let node: Element | null = element; node; node = node.parentElement) {
    const value = parseFloat(getComputedStyle(node).zoom);
    if (value > 0) zoom *= value;
  }
  return zoom;
}

/** Rectangle mis à l'échelle (repère de l'ancrage → repère de la popup). */
export function scaleRect(rect: PopperRect, ratio: number): PopperRect {
  return {
    x: rect.x * ratio,
    y: rect.y * ratio,
    width: rect.width * ratio,
    height: rect.height * ratio,
  };
}

/**
 * Modificateur Popper (Bootstrap : `popperConfig` ; Element Plus :
 * `popper-options`). Phase « beforeRead » : avant que Popper ne calcule la
 * position de la popup à partir du rectangle de l'ancrage.
 */
export const zoomedReferenceModifier = {
  name: "zoomedReference",
  enabled: true,
  phase: "beforeRead" as const,
  fn({
    state,
    name,
  }: {
    state: {
      elements: { reference: unknown; popper: HTMLElement };
      rects: { reference: PopperRect };
      modifiersData: Record<string, { scaled?: boolean }>;
    };
    name: string;
  }) {
    // Popper relance tous les modificateurs quand `flip` change de côté, sans
    // recalculer les rectangles : ne pas remettre à l'échelle une 2e fois.
    // (modifiersData est réinitialisé à chaque mise à jour, pas à la relance.)
    const data = state.modifiersData[name];
    if (data?.scaled) return;
    const { reference, popper } = state.elements;
    if (!(reference instanceof Element) || hasStandardCssZoom()) return;
    const ratio = effectiveZoom(reference) / effectiveZoom(popper);
    if (Math.abs(ratio - 1) < 0.001) return;
    state.rects.reference = scaleRect(state.rects.reference, ratio);
    state.modifiersData[name] = { ...data, scaled: true };
  },
};
